import React, { useState, useEffect, useCallback } from 'react';
import { RiCollapseDiagonalLine } from "react-icons/ri";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { FaFilter } from "react-icons/fa";
import { TbReload, TbArrowsSort } from "react-icons/tb";
import { FaDownload } from "react-icons/fa6";

const API_URL = process.env.REACT_APP_API_BASE|| '';
function StatusBadge({ status }) {
    const s = (status || '').toLowerCase();
    const map = {
        running:   'bg-yellow-100 text-yellow-700 border-yellow-200',
        completed: 'bg-green-100 text-green-700 border-green-200',
        failed:    'bg-red-100 text-red-600 border-red-200',
        pending:   'bg-gray-100 text-gray-500 border-gray-200',
        sent:      'bg-blue-100 text-blue-700 border-blue-200',
        delivered: 'bg-green-100 text-green-700 border-green-200',
        read:      'bg-purple-100 text-purple-700 border-purple-200',
    };
    return (
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border capitalize ${map[s] || 'bg-gray-100 text-gray-500 border-gray-200'}`}>
            {status || 'pending'}
        </span>
    );
}

function ApiReport() {
    const [showfilter, setshowfilter]         = useState(false);
    const [NumberFilter, setNumberFilter]     = useState('');
    const [TemplateType, setTemplateType]     = useState('');
    const [DeliveryStatus, setDeliveryStatus] = useState('');

    const [allData, setAllData]   = useState([]);   // flat list of campaign messages
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');

    const [currentPage, setcurrentPage] = useState(1);
    const [usersperpage, setUsersPerPage] = useState(10); // entries-per-page as state
    const entriesOptions = [10, 20, 50, 100];

    const indexoflastuser  = currentPage * usersperpage;
    const indexoffirstuser = indexoflastuser - usersperpage;
    const filteruser       = filtered.slice(indexoffirstuser, indexoflastuser);
    const totalpages       = Math.ceil(filtered.length / usersperpage);

    const [expandedRow, setExpandedRow] = useState(null);

    
    // ── Fetch: campaigns → messages for each ─────────────────────────────────
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res  = await fetch(`${API_URL}/auth/campaign/report`, { credentials: 'include' });
            const json = await res.json();
            if (!json.success) throw new Error('Failed');

            const campaigns = json.data || [];

            const allMessages = await Promise.all(
                campaigns.map(async (c) => {
                    try {
                        const r    = await fetch(`${API_URL}/auth/campaign/${c.id}`, { credentials: 'include' });
                        const data = await r.json();
                        return (data.data || []).map(msg => ({
                            ...msg,
                            campaign_id:    c.id,
                            campaign_name:  c.campaign_name,
                            template_name:  c.template_name,
                            template_type:  c.template_type,
                            interface_type: c.interface,
                            received_date:  c.created_at,
                        }));
                    } catch { return []; }
                })
            );

            const flat = allMessages.flat();
            setAllData(flat);
            setFiltered(flat);
        } catch (e) {
            setError('Failed to load API report data.');
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── Filter ────────────────────────────────────────────────────────────────
    const applyFilter = () => {
        let d = [...allData];
        if (NumberFilter)   d = d.filter(r => (r.phone_number || '').includes(NumberFilter));
        if (TemplateType)   d = d.filter(r => (r.template_type || '').toLowerCase() === TemplateType.toLowerCase());
        if (DeliveryStatus) d = d.filter(r => (r.status || '').toLowerCase() === DeliveryStatus.toLowerCase());
        setFiltered(d);
        setcurrentPage(1);
    };

    const clearFilter = () => {
        setNumberFilter(''); setTemplateType(''); setDeliveryStatus('');
        setFiltered(allData);
        setcurrentPage(1);
    };

    // ── CSV Download ──────────────────────────────────────────────────────────
    const handleDownload = () => {
        const headers = ['Received Date', 'Channel', 'Number', 'Status', 'Campaign', 'Template', 'Template Type', 'Interface', 'Variables'];
        const rows = filtered.map(r => {
            let vars = '';
            try { vars = JSON.parse(r.variables || '[]').join(' | '); } catch {}
            return [
                r.received_date ? new Date(r.received_date).toLocaleString() : '',
                'WhatsApp',
                r.phone_number || '',
                r.status || '',
                r.campaign_name || '',
                r.template_name || '',
                r.template_type || '',
                r.interface_type || '',
                vars,
            ];
        });
        const csv = [headers, ...rows].map(row => row.map(v => `"${v ?? ''}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const a    = document.createElement('a');
        a.href     = URL.createObjectURL(blob);
        a.download = 'api_report.csv';
        a.click();
    };

    const columns = ['Received Date', 'Channel', 'Number', 'Status', 'Campaign', 'Template Name', 'Interface / Type', 'Variables', 'Error Details', 'Action'];

    return (
        <>
            {/* Top Bar */}
            <div className='w-full flex items-center justify-between mb-2'>
                <p className='text-[12px] text-gray-500 dark:text-gray-400'>
                    Total: <span className='font-bold text-[#37715d]'>{allData.length}</span> messages
                </p>
                <div className='flex gap-2'>
                    <button
                        onClick={fetchData}
                        className='flex items-center border border-[#37715d] rounded-lg p-2 text-[#37715d] gap-1 hover:bg-[#37715d] hover:text-white transition-colors'
                    >
                        <TbReload className='text-[13px]' />
                        <span className='text-[12px] font-bold'>Refresh</span>
                    </button>
                    <button
                        onClick={handleDownload}
                        className='flex items-center border border-[#3f7fca] rounded-lg p-2 text-[#3f7fca] gap-1 hover:bg-[#3f7fca] hover:text-white transition-colors'
                    >
                        <FaDownload className='text-[12px]' />
                        <span className='text-[12px] font-bold'>Download</span>
                    </button>
                </div>
            </div>

            {/* Filter Bar */}
            <div
                onClick={() => setshowfilter(!showfilter)}
                className='w-full flex justify-between px-4 py-2 bg-[#e0e0e04b] rounded-t-[20px] cursor-pointer'
            >
                <div className='flex items-center text-[#576071] font-semibold gap-3 dark:text-white'>
                    <RiCollapseDiagonalLine /> Filter Records
                </div>
                <div className='flex items-center text-[#576071] dark:text-white'>
                    {showfilter ? <IoIosArrowDown /> : <IoIosArrowUp />}
                </div>
            </div>

            {showfilter && (
                <div className='bg-[#edf1f5] py-2 px-4 w-full dark:bg-[#121519] z-20'>
                    <div className='w-full flex flex-wrap gap-2'>

                        <div className='w-full sm:w-[48%] lg:w-[19%] flex flex-col gap-1'>
                            <label className='text-[#000] text-[12px] font-semibold dark:text-white'>Number</label>
                            <input
                                value={NumberFilter}
                                onChange={e => setNumberFilter(e.target.value)}
                                className='text-[#454546] border-[#d4cfcf] border rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-white'
                                type="text" placeholder="+91..."
                            />
                        </div>

                        <div className='w-full sm:w-[48%] lg:w-[19%] flex flex-col gap-1'>
                            <label className='text-[#000] text-[12px] font-semibold dark:text-white'>Template Type</label>
                            <select
                                value={TemplateType}
                                onChange={e => setTemplateType(e.target.value)}
                                className='text-[#454546] border-[#d4cfcf] border rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-white'
                            >
                                <option value="">Select Template Type</option>
                                <option value="marketing">Marketing</option>
                                <option value="utility">Utility</option>
                                <option value="authentication">Authentication</option>
                            </select>
                        </div>

                        <div className='w-full sm:w-[48%] lg:w-[19%] flex flex-col gap-1'>
                            <label className='text-[#000] text-[12px] font-semibold dark:text-white'>Delivery Status</label>
                            <select
                                value={DeliveryStatus}
                                onChange={e => setDeliveryStatus(e.target.value)}
                                className='text-[#454546] border-[#d4cfcf] border rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-white'
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="sent">Sent</option>
                                <option value="delivered">Delivered</option>
                                <option value="read">Read</option>
                                <option value="failed">Failed</option>
                            </select>
                        </div>

                        <div className='w-full sm:w-[48%] lg:w-[19%] flex items-center gap-1 mt-4'>
                            <button onClick={applyFilter} className='flex items-center border border-[#37715d] rounded-lg p-2 text-[#37715d] gap-2 hover:bg-[#37715d] hover:text-white w-full sm:w-auto transition-colors'>
                                <FaFilter /><span className='text-[12px] font-bold'>Filter</span>
                            </button>
                            <button onClick={clearFilter} className='flex items-center border border-[#3f7fca] rounded-lg p-2 text-[#3f7fca] gap-2 hover:bg-[#3f7fca] hover:text-white w-full sm:w-auto transition-colors'>
                                <TbReload /><span className='text-[12px] font-bold'>Clear Filter</span>
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {error && (
                <div className='w-full bg-red-50 border border-red-200 text-red-600 text-[12px] px-4 py-2 rounded my-2'>
                    {error}
                </div>
            )}

            {/* Table */}
            <div className='overflow-x-auto'>
                <table className="w-full overflow-hidden border border-[#dee2e6]">
                    <thead>
                        <tr className="text-left">
                            {columns.map(col => (
                                <th key={col} className="p-1 cursor-pointer border border-[#dee2e6] bg-[#f6f9fc] dark:bg-[#1a1f24]">
                                    <div className="flex justify-between items-center gap-2">
                                        <span className='text-[11px] text-[#576071] dark:text-white font-semibold'>{col}</span>
                                        {col !== 'Action' && (
                                            <button type="button" className="text-gray-400 hover:text-black"><TbArrowsSort /></button>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr className="bg-[#f6f9fc] dark:bg-[#121519]">
                                <td colSpan={columns.length} className="py-8 text-center">
                                    <div className="flex items-center justify-center gap-2 text-[12px] text-gray-400">
                                        <div className="w-4 h-4 border-2 border-[#37715d] border-t-transparent rounded-full animate-spin" />
                                        Loading API report...
                                    </div>
                                </td>
                            </tr>
                        ) : filteruser.length === 0 ? (
                            <tr className="text-center bg-[#f6f9fc] dark:bg-[#121519]">
                                <td colSpan={columns.length} className="py-8 text-[12px] text-gray-400">No data found</td>
                            </tr>
                        ) : (
                            filteruser.map((row, i) => {
                                let vars = '-';
                                try {
                                    const parsed = JSON.parse(row.variables || '[]');
                                    vars = parsed.length ? parsed.join(', ') : '-';
                                } catch {}

                                return (
                                    <>
                                   
                                    <tr
                                        key={row.id || i}
                                        className="text-gray-500 odd:bg-white even:bg-[#edf1f5] dark:odd:bg-[#121519] dark:even:bg-[#1a1f24] hover:bg-[#e8f0fa] dark:hover:bg-[#2a2f35] transition-colors"
                                    >
                                        <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white text-gray-400">
                                            {row.received_date ? new Date(row.received_date).toLocaleString() : '-'}
                                        </td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white">
                                            <span className='flex items-center gap-1'>
                                                <span className='w-2 h-2 rounded-full bg-green-500 inline-block'></span>
                                                WhatsApp
                                            </span>
                                        </td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white font-mono">
                                            {row.phone_number || '-'}
                                        </td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6]">
                                            <StatusBadge status={row.status} />
                                        </td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white">
                                            <div className='font-medium'>{row.campaign_name || '-'}</div>
                                            <div className='text-[10px] text-gray-400'>ID: {row.campaign_id}</div>
                                        </td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white">
                                            <div>{row.template_name || '-'}</div>
                                            {row.template_type && (
                                                <span className='text-[9px] text-gray-400 capitalize'>{row.template_type}</span>
                                            )}
                                        </td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white capitalize">
                                            {row.interface_type || 'web'}
                                        </td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white text-gray-400 max-w-[100px] truncate" title={vars}>
                                            {vars}
                                        </td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6]">
                                            {row.status === 'failed' ? (
                                                <span className='text-red-500 text-[10px]'>Send failed</span>
                                            ) : '-'}
                                        </td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6]">
    <button
        onClick={() => setExpandedRow(expandedRow === (row.id || i) ? null : (row.id || i))}
        className={`hover:text-white border rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${
            expandedRow === (row.id || i)
                ? 'bg-[#3f7fca] text-white border-[#3f7fca]'
                : 'text-[#3f7fca] hover:bg-[#3f7fca] border-[#3f7fca]'
        }`}
    >
        {expandedRow === (row.id || i) ? 'Close' : 'View'}
    </button>
</td>
                                    </tr>

{expandedRow === (row.id || i) && (
    <tr className="bg-[#f0f6ff] dark:bg-[#1e2530]">
        <td colSpan={columns.length} className="p-3 border border-[#dee2e6]">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-[11px]">
                <div>
                   
                </div>
                <div>
                    <p className="text-gray-400 font-semibold uppercase tracking-wide text-[9px] mb-0.5">Campaign Name</p>
                    <p className="dark:text-white">{row.campaign_name || '-'}</p>
                </div>
                <div>
                    <p className="text-gray-400 font-semibold uppercase tracking-wide text-[9px] mb-0.5">Template Name</p>
                    <p className="dark:text-white">{row.template_name || '-'}</p>
                </div>
                <div>
                    <p className="text-gray-400 font-semibold uppercase tracking-wide text-[9px] mb-0.5">Template Type</p>
                    <p className="dark:text-white capitalize">{row.template_type || '-'}</p>
                </div>
                <div>
                    <p className="text-gray-400 font-semibold uppercase tracking-wide text-[9px] mb-0.5">Interface</p>
                    <p className="dark:text-white capitalize">{row.interface_type || 'web'}</p>
                </div>
                <div>
                    <p className="text-gray-400 font-semibold uppercase tracking-wide text-[9px] mb-0.5">Phone Number</p>
                    <p className="dark:text-white font-mono">{row.phone_number || '-'}</p>
                </div>
                <div>
                    <p className="text-gray-400 font-semibold uppercase tracking-wide text-[9px] mb-0.5">Status</p>
                    <StatusBadge status={row.status} />
                </div>
                <div>
                    <p className="text-gray-400 font-semibold uppercase tracking-wide text-[9px] mb-0.5">Received Date</p>
                    <p className="dark:text-white">{row.received_date ? new Date(row.received_date).toLocaleString() : '-'}</p>
                </div>
                <div className="col-span-2 sm:col-span-3 lg:col-span-4">
                    <p className="text-gray-400 font-semibold uppercase tracking-wide text-[9px] mb-0.5">Variables</p>
                    <p className="dark:text-white break-all">{vars !== '-' ? vars : 'No variables'}</p>
                </div>
                {row.status === 'failed' && (
                    <div className="col-span-2 sm:col-span-3 lg:col-span-4">
                        <p className="text-gray-400 font-semibold uppercase tracking-wide text-[9px] mb-0.5">Error Details</p>
                        <p className="text-red-500">{row.error_message || 'Send failed — no details available'}</p>
                    </div>
                )}
            </div>
        </td>
    </tr>
)}
 </>
                                    
                                );
                               
                            })
                        )}
                    </tbody>
                </table>
            </div>
            

            {/* Pagination */}
            <div className='flex flex-col sm:flex-row justify-between w-full mt-2 items-start sm:items-center gap-2'>
                <div className='flex items-center gap-2'>
                    <span className='text-[11px] text-[#3f7fc8]'>Show</span>
                    <select
                        className="text-[11px] border border-gray-300 rounded px-2 py-1 bg-white dark:bg-[#121519] dark:text-white"
                        value={usersperpage}
                        onChange={e => {
                            setUsersPerPage(Number(e.target.value));
                            setcurrentPage(1);
                        }}
                    >
                        {entriesOptions.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                    <span className='text-[11px] text-[#3f7fc8]'>entries</span>
                </div>
                <p className='text-[11px] text-[#3f7fc8] mt-2 sm:mt-0'>
                    Showing {filtered.length === 0 ? 0 : indexoffirstuser + 1} to {Math.min(indexoflastuser, filtered.length)} of {filtered.length} records
                </p>
                <div className="flex items-center mt-2 sm:mt-0 space-x-2">
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setcurrentPage(p => p - 1)}
                        className="px-3 py-1 text-[11px] bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300 transition"
                    >Prev</button>
                    <p className='text-[11px] text-[#3f7fc8]'>{currentPage} page of {totalpages || 1} pages</p>
                    <button
                        disabled={currentPage >= totalpages}
                        onClick={() => setcurrentPage(p => p + 1)}
                        className="px-3 py-1 text-[11px] bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300 transition"
                    >Next</button>
                </div>
            </div>
        </>
    );
}

export default ApiReport;